import {
  getNoteAtPosition,
  type Degree,
  type NeckPosition,
  type NoteName,
} from "../guitarEngine";

/** Modo de evaluación de la misión. */
export type ChordPathEvalMode = "strum-poly" | "arpeggio-mono";

export type ChordPathShapeId = "Em" | "E" | "Am";

export interface ChordPathArpeggioStep {
  string: number;
  fret: number;
  midi: number;
  note: NoteName;
  octave: number;
  label: string;
}

export interface ChordPathMission {
  id: string;
  order: number;
  title: string;
  subtitle: string;
  eyebrow: string;
  description: string;
  goal: string;
  instructions: string[];
  professorTip: string;
  feedbackOk: string;
  feedbackError: string;
  timeEstimateMin: number;
  shapeId: ChordPathShapeId;
  evalMode: ChordPathEvalMode;
  /** Pitch classes (0–11) esperados en rasgueo polifónico. */
  requiredPitchClasses?: number[];
  /** MIDIs concretos (p. ej. E2+B2) — se acepta ±1. */
  requiredMidis?: number[];
  /** Mínimo de clases / midis acertados para aprobar. */
  minHits: number;
  unlockRequires?: string[];
}

/** Diagrama de mástil para el camino (dedos pedagógicos). */
export type ChordPathDiagram = {
  shapeId: ChordPathShapeId;
  label: string;
  positions: NeckPosition[];
  mutedStrings: number[];
  /** Notas del rasgueo (excluye muteadas). */
  playNotes: { string: number; fret: number }[];
};

function pos(
  string: number,
  fret: number,
  finger: number | undefined,
  degree: Degree,
  highlight?: NeckPosition["highlight"]
): NeckPosition {
  const n = getNoteAtPosition(string, fret);
  return {
    string,
    fret,
    note: n.note,
    degree,
    finger: finger && finger > 0 ? finger : undefined,
    highlight: highlight ?? (degree === "R" ? "root" : degree === "5" ? "fifth" : "third"),
  };
}

export const CHORD_PATH_DIAGRAMS: Record<ChordPathShapeId, ChordPathDiagram> = {
  Em: {
    shapeId: "Em",
    label: "Mi menor",
    mutedStrings: [],
    positions: [
      pos(6, 0, 0, "R", "root"),
      pos(5, 2, 2, "5", "fifth"),
      pos(4, 2, 3, "R", "root"),
      pos(3, 0, 0, "b3", "third"),
      pos(2, 0, 0, "5", "fifth"),
      pos(1, 0, 0, "R", "root"),
    ],
    playNotes: [
      { string: 6, fret: 0 },
      { string: 5, fret: 2 },
      { string: 4, fret: 2 },
      { string: 3, fret: 0 },
      { string: 2, fret: 0 },
      { string: 1, fret: 0 },
    ],
  },
  E: {
    shapeId: "E",
    label: "Mi mayor",
    mutedStrings: [],
    positions: [
      pos(6, 0, 0, "R", "root"),
      pos(5, 2, 2, "5", "fifth"),
      pos(4, 2, 3, "R", "root"),
      pos(3, 1, 1, "3", "third"),
      pos(2, 0, 0, "5", "fifth"),
      pos(1, 0, 0, "R", "root"),
    ],
    playNotes: [
      { string: 6, fret: 0 },
      { string: 5, fret: 2 },
      { string: 4, fret: 2 },
      { string: 3, fret: 1 },
      { string: 2, fret: 0 },
      { string: 1, fret: 0 },
    ],
  },
  Am: {
    shapeId: "Am",
    label: "La menor",
    mutedStrings: [6],
    positions: [
      pos(5, 0, 0, "R", "root"),
      pos(4, 2, 2, "5", "fifth"),
      pos(3, 2, 3, "R", "root"),
      pos(2, 1, 1, "b3", "third"),
      pos(1, 0, 0, "5", "fifth"),
    ],
    playNotes: [
      { string: 5, fret: 0 },
      { string: 4, fret: 2 },
      { string: 3, fret: 2 },
      { string: 2, fret: 1 },
      { string: 1, fret: 0 },
    ],
  },
};

/** Arpegio Em nota a nota (auditoría de limpieza). */
export const EM_ARPEGGIO_STEPS: ChordPathArpeggioStep[] = [
  { string: 6, fret: 0, midi: 40, note: "E", octave: 2, label: "6ª al aire · E2" },
  { string: 5, fret: 2, midi: 47, note: "B", octave: 2, label: "5ª traste 2 · B2" },
  { string: 4, fret: 2, midi: 52, note: "E", octave: 3, label: "4ª traste 2 · E3" },
  { string: 3, fret: 0, midi: 55, note: "G", octave: 3, label: "3ª al aire · G3" },
  { string: 2, fret: 0, midi: 59, note: "B", octave: 3, label: "2ª al aire · B3" },
  { string: 1, fret: 0, midi: 64, note: "E", octave: 4, label: "1ª al aire · E4" },
];

export const CHORD_PATH_MISSIONS: ChordPathMission[] = [
  {
    id: "chords-01-em",
    order: 1,
    title: "El Acorde Base (Mi menor)",
    subtitle: "Tu primera postura con la mano izquierda",
    eyebrow: "Camino 2 · Familia Mi y La",
    description:
      "Construye Em con los dedos 2 y 3, dejando la 6ª al aire como tónica.",
    goal: "Coloca los dedos formando un bloque para que suenen 6 cuerdas a la vez.",
    instructions: [
      "Observa el mástil: Dedo 2 en la 5ª cuerda y Dedo 3 en la 4ª cuerda (ambos en el traste 2).",
      "Arquea los dedos como si sostuvieras una manzana.",
      "Pasa el pulgar derecho por todas las cuerdas, desde la 6ª hacia abajo.",
    ],
    professorTip:
      "Es normal que al principio las yemas duelan un poco y el sonido no sea perfecto. Busca que el pulgar de tu mano izquierda esté en la mitad del mástil por detrás, no asomando por arriba.",
    feedbackOk: "¡Em plantado! La tónica y la quinta ya suenan juntas.",
    feedbackError:
      "Aún no se oyen claro la fundamental (E grave) y la quinta (B). Revisa el arqueo y rasguea de nuevo.",
    timeEstimateMin: 6,
    shapeId: "Em",
    evalMode: "strum-poly",
    // E2 + B2 (criterio pedagógico suave)
    requiredMidis: [40, 47],
    requiredPitchClasses: [4, 11],
    minHits: 2,
  },
  {
    id: "chords-02-e",
    order: 2,
    title: "El Dedo Faltante (Mi mayor)",
    subtitle: "Un solo dedo cambia todo el color",
    eyebrow: "Camino 2 · Familia Mi y La",
    description: "Transforma Em en E añadiendo el índice en la 3ª cuerda, traste 1.",
    goal: "Añade el dedo índice a la postura anterior para crear un acorde brillante (Mayor).",
    instructions: [
      "Mantén los dedos 2 y 3 donde estaban en el Mi menor.",
      "Pisa la 3ª cuerda en el traste 1 con tu dedo 1 (índice).",
      "Toca las 6 cuerdas hacia abajo.",
    ],
    professorTip:
      "Presta atención a la diferencia de sonido. El Mi menor sonaba melancólico, este Mi mayor suena luminoso. Ese cambio lo hace una sola nota.",
    feedbackOk: "¡Color mayor! El índice encendió el acorde.",
    feedbackError:
      "Falta la tercera mayor (G♯) o el acorde suena incompleto. Revisa el dedo 1 en la 3ª cuerda.",
    timeEstimateMin: 6,
    shapeId: "E",
    evalMode: "strum-poly",
    requiredPitchClasses: [4, 8, 11], // E, G#, B
    minHits: 2,
    unlockRequires: ["chords-01-em"],
  },
  {
    id: "chords-03-am",
    order: 3,
    title: "El Ascensor (La menor)",
    subtitle: "La misma forma, un piso más abajo",
    eyebrow: "Camino 2 · Familia Mi y La",
    description:
      "Trasladá la geometría de E una cuerda hacia abajo para formar Am, sin tocar la 6ª.",
    goal: "Trasladá el bloque de tus dedos sin desarmarlo.",
    instructions: [
      "Toma la figura del Mi mayor y baja TODOS los dedos una cuerda hacia el suelo.",
      "El dedo 1 queda en la 2ª cuerda, dedos 2 y 3 en la 4ª y 3ª.",
      "¡Importante! Toca desde la 5ª cuerda hacia abajo. No toques la más gruesa.",
    ],
    professorTip:
      "Mover los dedos en bloque es el secreto para cambiar de acordes rápido en el futuro.",
    feedbackOk: "¡Ascensor listo! Misma forma, nuevo piso: La menor.",
    feedbackError:
      "No se oyeron claras las notas de Am (A–C–E). Baja el bloque entero y evita la 6ª.",
    timeEstimateMin: 6,
    shapeId: "Am",
    evalMode: "strum-poly",
    requiredPitchClasses: [9, 0, 4], // A, C, E
    minHits: 2,
    unlockRequires: ["chords-02-e"],
  },
  {
    id: "chords-04-arpeggio",
    order: 4,
    title: "Rayos X de Sonido",
    subtitle: "Prueba cuerda a cuerda",
    eyebrow: "Camino 2 · Auditoría de sonido",
    description:
      "Arpegia Em nota a nota para comprobar que ninguna cuerda suena apagada.",
    goal: "Arpegia el Mi menor nota a nota para comprobar que ninguna suena «muerta» o apagada.",
    instructions: [
      "Arma el acorde de Mi menor (Em).",
      "Toca una sola vez la cuerda que te indica la pantalla y espera a que se marque en verde.",
      "Si la cuerda suena apagada, corrige la postura de tus dedos y vuelve a intentarlo.",
    ],
    professorTip:
      "Si una cuerda no suena, casi siempre es porque la parte de abajo de un dedo la está tocando por accidente. Levanta un poco más la muñeca.",
    feedbackOk: "¡Rayos X limpios! Las seis cuerdas de Em suenan claras.",
    feedbackError:
      "Alguna cuerda no pasó la auditoría. Revisa el arqueo y vuelve a intentar la secuencia.",
    timeEstimateMin: 7,
    shapeId: "Em",
    evalMode: "arpeggio-mono",
    minHits: 6,
    unlockRequires: ["chords-03-am"],
  },
];

export function getChordPathMissionById(id: string): ChordPathMission | undefined {
  return CHORD_PATH_MISSIONS.find((m) => m.id === id);
}

export function isChordPathMissionUnlocked(
  mission: ChordPathMission,
  completedMissionIds: string[],
  preview = false
): boolean {
  if (preview) return true;
  if (!mission.unlockRequires?.length) return true;
  return mission.unlockRequires.every((id) => completedMissionIds.includes(id));
}

export function getFirstUnlockedChordPathMission(
  completedMissionIds: string[]
): ChordPathMission | undefined {
  return CHORD_PATH_MISSIONS.find(
    (m) =>
      isChordPathMissionUnlocked(m, completedMissionIds) &&
      !completedMissionIds.includes(m.id)
  );
}

export function getDiagramForMission(mission: ChordPathMission): ChordPathDiagram {
  return CHORD_PATH_DIAGRAMS[mission.shapeId];
}
